import { z } from "zod";
export const activationEnvironmentSchema = z.enum([
    "global_production",
    "global_sandbox",
    "sfoa_production",
    "sfoa_sandbox"
]);
export const activationCodeEnvironmentSchema = z.union([
    activationEnvironmentSchema,
    z.literal("auto_detect")
]);
export const salesforceEnvironmentProfileStatusSchema = z.enum([
    "draft",
    "active",
    "disabled"
]);
export const salesforceEnvironmentProfileSchema = z.object({
    id: z.string().uuid(),
    environment: activationEnvironmentSchema,
    displayName: z.string().trim().min(2).max(120),
    loginUrl: z.string().url().max(500),
    callbackUrl: z.string().url().max(1000).optional(),
    status: salesforceEnvironmentProfileStatusSchema,
    note: z.string().trim().max(500).optional(),
    version: z.number().int().min(1),
    createdByEmail: z.string().email().max(320),
    updatedByEmail: z.string().email().max(320),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const updateSalesforceEnvironmentProfileInputSchema = z.object({
    displayName: z.string().trim().min(2).max(120),
    loginUrl: z.string().url().max(500),
    callbackUrl: z.string().url().max(1000).nullable().optional(),
    status: salesforceEnvironmentProfileStatusSchema,
    note: z.string().trim().max(500).nullable().optional(),
    expectedVersion: z.number().int().min(1)
});
export const salesforceEnvironmentProfileReadinessSchema = z.object({
    profile: salesforceEnvironmentProfileSchema,
    readiness: z.enum(["ready", "needs_attention", "disabled"]),
    checks: z.object({
        trustedLoginDomain: z.boolean(),
        callbackConfigured: z.boolean(),
        callbackMatchesRuntime: z.boolean(),
        loginMatchesRuntime: z.boolean(),
        oauthClientConfigured: z.boolean()
    }),
    effectiveCallbackUrl: z.string().url().max(1000).optional(),
    messages: z.array(z.string().max(300)).max(10)
});
export const activationFeatureKeySchema = z.enum([
    "assistant",
    "web_workspace",
    "scenario_studio",
    "write_actions",
    "knowledge"
]);
export const activationCodeStatusSchema = z.enum([
    "unused",
    "used",
    "revoked",
    "expired"
]);
export const operationsCustomerStatusSchema = z.enum([
    "prospect",
    "active",
    "suspended",
    "closed"
]);
const operationsCustomerDetailsSchema = z.object({
    name: z.string().trim().min(2).max(160),
    status: operationsCustomerStatusSchema,
    primaryContactName: z.string().trim().min(1).max(120),
    contactEmail: z.string().email().max(320),
    contactPhone: z.string().trim().max(40).optional(),
    countryRegion: z.string().trim().min(1).max(120),
    address: z.string().trim().max(300).optional(),
    contractStartDate: z.string().date().optional(),
    contractEndDate: z.string().date().optional(),
    note: z.string().trim().max(1000).optional()
});
export const operationsCustomerSchema = operationsCustomerDetailsSchema.extend({
    id: z.string().uuid(),
    customerCode: z.string().regex(/^CUS-\d{8}-[A-Z0-9]{4,8}$/),
    version: z.number().int().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
}).superRefine((customer, context) => {
    if (customer.contractStartDate && customer.contractEndDate &&
        customer.contractEndDate < customer.contractStartDate) {
        context.addIssue({
            code: "custom",
            path: ["contractEndDate"],
            message: "合同结束日期不能早于开始日期"
        });
    }
});
export const createOperationsCustomerInputSchema = operationsCustomerDetailsSchema.superRefine((customer, context) => {
    if (customer.contractStartDate && customer.contractEndDate &&
        customer.contractEndDate < customer.contractStartDate) {
        context.addIssue({
            code: "custom",
            path: ["contractEndDate"],
            message: "合同结束日期不能早于开始日期"
        });
    }
});
export const updateOperationsCustomerInputSchema = operationsCustomerDetailsSchema.partial().and(z.object({ expectedVersion: z.number().int().min(1) }));
export const activationCodeSchema = z.object({
    id: z.string().uuid(),
    customerId: z.string().uuid(),
    preview: z.string().min(10).max(80),
    status: activationCodeStatusSchema,
    environment: activationCodeEnvironmentSchema,
    environmentProfileId: z.string().uuid().optional(),
    expiresAt: z.string().datetime(),
    seatLimit: z.number().int().min(1).max(10_000),
    featureKeys: z.array(activationFeatureKeySchema).min(1).max(10),
    note: z.string().trim().max(500).optional(),
    expectedOrganizationId: z.string().min(15).max(18).optional(),
    expectedSalesforceUserId: z.string().min(15).max(18).optional(),
    boundOrganizationId: z.string().min(15).max(18).optional(),
    boundSalesforceUserId: z.string().min(15).max(18).optional(),
    createdByEmail: z.string().email().max(320),
    createdAt: z.string().datetime(),
    redeemedAt: z.string().datetime().optional(),
    revokedAt: z.string().datetime().optional()
});
export const createActivationCodeInputSchema = z.object({
    environment: activationCodeEnvironmentSchema,
    environmentProfileId: z.string().uuid().optional(),
    expiresAt: z.string().datetime(),
    seatLimit: z.number().int().min(1).max(10_000),
    featureKeys: z.array(activationFeatureKeySchema).min(1).max(10),
    note: z.string().trim().max(500).optional(),
    expectedOrganizationId: z.string().trim().regex(/^00D[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/, "请输入 15 或 18 位 Salesforce Org ID（以 00D 开头）"),
    expectedSalesforceUserId: z.string().trim().regex(/^005[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/, "Salesforce User ID 必须为 15 或 18 位并以 005 开头").optional()
}).superRefine((input, context) => {
    if (Date.parse(input.expiresAt) <= Date.now()) {
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "激活码有效期必须晚于当前时间"
        });
    }
});
export const createCustomerActivationCodeInputSchema = z.object({
    expiresAt: z.string().datetime(),
    seatLimit: z.number().int().min(1).max(10_000),
    featureKeys: z.array(activationFeatureKeySchema).min(1).max(10),
    note: z.string().trim().max(500).optional(),
    expectedOrganizationId: z.string().trim().regex(/^00D[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/, "请输入 15 或 18 位 Salesforce Org ID（以 00D 开头）"),
    expectedSalesforceUserId: z.string().trim().regex(/^005[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/, "请输入 15 或 18 位 Salesforce User ID（以 005 开头）")
}).superRefine((input, context) => {
    if (Date.parse(input.expiresAt) <= Date.now()) {
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "激活码有效期必须晚于当前时间"
        });
    }
});
export const organizationEntitlementStatusSchema = z.enum([
    "pending",
    "active",
    "suspended",
    "expired",
    "revoked"
]);
export const organizationEntitlementSchema = z.object({
    organizationId: z.string().min(15).max(18),
    customerId: z.string().uuid().optional(),
    displayName: z.string().trim().min(1).max(160),
    environment: activationEnvironmentSchema,
    status: organizationEntitlementStatusSchema,
    validFrom: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
    seatLimit: z.number().int().min(1).max(10_000),
    featureKeys: z.array(activationFeatureKeySchema).min(1).max(10),
    activationCodeId: z.string().uuid().optional(),
    primaryAdminSalesforceUserId: z.string().min(15).max(18).optional(),
    suspensionReason: z.string().trim().max(500).optional(),
    version: z.number().int().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
}).superRefine((entitlement, context) => {
    if (entitlement.expiresAt &&
        Date.parse(entitlement.expiresAt) <= Date.parse(entitlement.validFrom)) {
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "组织授权到期时间必须晚于生效时间"
        });
    }
});
export const operationsCustomerDetailSchema = z.object({
    customer: operationsCustomerSchema,
    activationCodes: z.array(activationCodeSchema),
    organizations: z.array(organizationEntitlementSchema)
});
export const updateOrganizationEntitlementInputSchema = z.object({
    status: organizationEntitlementStatusSchema,
    expiresAt: z.string().datetime().nullable().optional(),
    seatLimit: z.number().int().min(1).max(10_000).optional(),
    featureKeys: z.array(activationFeatureKeySchema).min(1).max(10).optional(),
    suspensionReason: z.string().trim().max(500).nullable().optional(),
    expectedVersion: z.number().int().min(1)
});
export const operationsAuditEventSchema = z.object({
    id: z.string().uuid(),
    actorEmail: z.string().email().max(320),
    action: z.string().min(1).max(120),
    targetType: z.string().min(1).max(80),
    targetId: z.string().min(1).max(200),
    outcome: z.enum(["success", "denied", "failure"]),
    details: z.record(z.string(), z.unknown()),
    createdAt: z.string().datetime()
});
export const operationsOverviewSchema = z.object({
    unusedActivationCodes: z.number().int().nonnegative(),
    expiringActivationCodes: z.number().int().nonnegative(),
    activeOrganizations: z.number().int().nonnegative(),
    suspendedOrganizations: z.number().int().nonnegative(),
    generatedAt: z.string().datetime()
});
